import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CreditCard, Clock, CheckCircle, AlertCircle, Info, X, Package, Plus } from 'lucide-react';
import api from '../utils/api';
import { formatHours } from '../utils/timeFormatting';
import {
  StudentHourPackage,
  Subject, 
  PackageOption, 
  HourPackageType,
  formatPackageType,
  getPackageStatusColor
} from '../types/hourPackages';

interface Payment {
  id: string;
  amount: number;
  status: 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  dueDate: string;
  paidAt?: string;
  createdAt: string;
  booking: {
    id: string;
    startTime: string;
    endTime: string;
    type?: 'LESSON' | 'CONSULTATION';
    subject: {
      name: string;
      category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
    };
    tutor: {
      name: string;
    };
  };
  confirmedByUser?: {
    name: string;
  };
}

// Lessons that run consecutively (each starting within ~30 min of the
// previous one ending) are treated as "back-to-back" and offered as a single
// combined payment, so the student pays one Stripe fee instead of one per
// lesson (e.g. one £60 charge rather than £30 + £30).
const BACK_TO_BACK_MAX_GAP_MS = 30 * 60 * 1000;

// Group payments into back-to-back blocks, ordered by lesson start time.
// A block with 2+ lessons becomes one combined "pay for all" button.
const groupBackToBack = (items: Payment[]): Payment[][] => {
  const sorted = [...items].sort(
    (a, b) => new Date(a.booking.startTime).getTime() - new Date(b.booking.startTime).getTime(),
  );
  const groups: Payment[][] = [];
  for (const payment of sorted) {
    const current = groups[groups.length - 1];
    if (current) {
      const prevEnd = new Date(current[current.length - 1].booking.endTime).getTime();
      const thisStart = new Date(payment.booking.startTime).getTime();
      const gap = thisStart - prevEnd;
      // Allow a 1-min slop for rounding; cap at the back-to-back gap.
      if (gap >= -60 * 1000 && gap <= BACK_TO_BACK_MAX_GAP_MS) {
        current.push(payment);
        continue;
      }
    }
    groups.push([payment]);
  }
  return groups;
};

const PaymentsPage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [statusBanner, setStatusBanner] = useState<{ kind: 'success' | 'cancel'; message: string } | null>(null);
  const [startingCheckoutId, setStartingCheckoutId] = useState<string | null>(null);

  // Hour package states
  const [hourPackages, setHourPackages] = useState<StudentHourPackage[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [selectedDelivery, setSelectedDelivery] = useState<'online' | 'in-person'>('online');
  const [packageOptions, setPackageOptions] = useState<PackageOption[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [purchasingPackage, setPurchasingPackage] = useState(false);
  const [maxDiscount, setMaxDiscount] = useState<number>(0);
  const [purchaseError, setPurchaseError] = useState<string>('');

  useEffect(() => {
    if (user) {
      fetchPayments();
      fetchHourPackages();
      fetchSubjects();
      fetchMaxDiscount();
      const hasSeenPaymentInfo = localStorage.getItem('hasSeenPaymentInfo');
      if (!hasSeenPaymentInfo) {
        setShowInfoModal(true);
      }
    }
  }, [user]);

  // Handle Stripe Checkout return via ?status=success|cancel
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    if (status === 'success') {
      setStatusBanner({
        kind: 'success',
        message: 'Payment successful — it may take a few seconds to show as paid below.',
      });
      fetchPayments();
      fetchHourPackages();
      navigate('/payments', { replace: true });
    } else if (status === 'cancel') {
      setStatusBanner({
        kind: 'cancel',
        message: 'Payment cancelled. You can try again anytime.',
      });
      navigate('/payments', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/payments/student/${user?.id}`);
      setPayments(response.data);
    } catch (error: any) {
      console.error('Failed to fetch payments:', error);
      setError('Failed to load payments. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchHourPackages = async () => {
    try {
      const response = await api.get(`/hour-packages/student/${user?.id}`);
      setHourPackages(response.data);
    } catch (error: any) {
      console.error('Failed to fetch hour packages:', error);
      
      // Check for database table errors
      if (error.response?.data?.message?.includes('table not found') || 
          error.response?.data?.message?.includes('migration')) {
        console.error('DATABASE ERROR: Hour packages table does not exist');
        setError('Hour packages feature is not available. Database migration may be needed.');
      }
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await api.get('/subjects');
      const all: Subject[] = Array.isArray(response.data) ? response.data : [];
      // Limit hour-package purchases to subjects the student picked on
      // signup / in their profile. Avoids showing them rates for subjects
      // they're never going to book — and keeps the dropdown short.
      const profileSubjectIds = new Set(
        (user?.studentProfile?.subjects ?? []).map((s) => s.id),
      );
      const filtered = profileSubjectIds.size > 0
        ? all.filter((s) => profileSubjectIds.has(s.id))
        : all;
      setSubjects(filtered);
    } catch (error: any) {
      console.error('Failed to fetch subjects:', error);
    }
  };

  const fetchMaxDiscount = async () => {
    try {
      // Fetch package configs (public endpoint — the tutor-only /configs
      // route 403s for students) to get the maximum discount available
      const response = await api.get('/hour-packages/configs/public');
      if (response.data && response.data.length > 0) {
        const maxDiscountValue = Math.max(...response.data.map((config: any) => config.discountPercent || 0));
        setMaxDiscount(maxDiscountValue);
      }
    } catch (error: any) {
      console.error('Failed to fetch max discount:', error);
      // Default to 20 if we can't fetch the configs
      setMaxDiscount(20);
    }
  };

  const fetchPackageOptions = async (subjectId: string, isOnline: boolean) => {
    try {
      setLoadingPackages(true);
      const response = await api.get(`/hour-packages/pricing/${subjectId}?isOnline=${isOnline}`);
      setPackageOptions(response.data.packages);
      
      // Calculate and set maximum discount
      if (response.data.packages && response.data.packages.length > 0) {
        const maxDiscountValue = Math.max(...response.data.packages.map((pkg: PackageOption) => pkg.discountPercent || 0));
        setMaxDiscount(maxDiscountValue);
      }
    } catch (error: any) {
      console.error('Failed to fetch package options:', error);
    } finally {
      setLoadingPackages(false);
    }
  };

  const handlePurchasePackage = async (packageType: HourPackageType) => {
    if (!selectedSubject) return;

    setPurchasingPackage(true);
    setPurchaseError('');

    try {
      const response = await api.post('/hour-packages/purchase', {
        subjectId: selectedSubject.id,
        packageType,
        isOnline: selectedDelivery === 'online',
      });

      if (!response.data.package || !response.data.package.id) {
        setPurchaseError('Package creation failed - please contact support.');
        return;
      }
      if (!response.data.checkoutUrl) {
        setPurchaseError('Could not start Stripe checkout - please try again.');
        return;
      }

      // Redirect to Stripe Checkout. On return we land on /payments?status=success|cancel.
      window.location.href = response.data.checkoutUrl;
    } catch (error: any) {
      console.error('Failed to purchase package:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      setPurchaseError(`Failed to purchase package: ${errorMessage}. Please try again or contact support.`);
    } finally {
      setPurchasingPackage(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Function to check if payment is for same-day booking
  const isSameDayPayment = (payment: Payment) => {
    const today = new Date();
    const lessonDate = new Date(payment.booking.startTime);
    return lessonDate.toDateString() === today.toDateString();
  };

  // Function to check if payment is urgent (due within 2 hours)
  const isUrgentPayment = (payment: Payment) => {
    const now = new Date();
    const dueDate = new Date(payment.dueDate);
    const timeDiff = dueDate.getTime() - now.getTime();
    const hoursDiff = timeDiff / (1000 * 60 * 60);
    return hoursDiff <= 2 && hoursDiff > 0;
  };

  const formatSubjectCategory = (category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY') => {
    switch (category) {
      case 'ELEVEN_PLUS':
        return '11+';
      case 'GCSE':
        return 'GCSE';
      case 'A_LEVEL':
        return 'A-Level';
      case 'UNIVERSITY':
        return 'University';
      default:
        return category;
    }
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'PAID':
        return 'bg-green-100 text-green-800';
      case 'OVERDUE':
        return 'bg-red-100 text-red-800';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="w-4 h-4" />;
      case 'PAID':
        return <CheckCircle className="w-4 h-4" />;
      case 'OVERDUE':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const handlePaymentClick = async (paymentId: string) => {
    setStartingCheckoutId(paymentId);
    setError('');
    try {
      const { data } = await api.post(`/payments/${paymentId}/checkout-session`);
      if (!data?.url) {
        setError('Could not start checkout. Please try again.');
        return;
      }
      window.location.href = data.url;
    } catch (e: any) {
      console.error('Failed to start checkout:', e);
      setError(e.response?.data?.message || 'Could not start checkout. Please try again.');
    } finally {
      setStartingCheckoutId(null);
    }
  };

  // Pay for a whole back-to-back block in one Stripe checkout. The synthetic
  // key (joined payment ids) drives the button's loading state.
  const handleGroupPaymentClick = async (group: Payment[]) => {
    const key = group.map((p) => p.id).join(',');
    setStartingCheckoutId(key);
    setError('');
    try {
      const { data } = await api.post('/payments/group-checkout-session', {
        paymentIds: group.map((p) => p.id),
      });
      if (!data?.url) {
        setError('Could not start checkout. Please try again.');
        return;
      }
      window.location.href = data.url;
    } catch (e: any) {
      console.error('Failed to start combined checkout:', e);
      setError(e.response?.data?.message || 'Could not start checkout. Please try again.');
    } finally {
      setStartingCheckoutId(null);
    }
  };

  const handleCloseInfoModal = () => {
    setShowInfoModal(false);
    localStorage.setItem('hasSeenPaymentInfo', 'true');
  };

  const pendingPayments = payments.filter(payment => payment.status === 'PENDING');
  const paidPayments = payments.filter(payment => payment.status === 'PAID');
  const overduePayments = payments.filter(payment => payment.status === 'OVERDUE');

  // Group pending payments into back-to-back blocks, then sort the blocks so
  // same-day / urgent ones surface first (mirroring the per-payment ordering).
  const groupSameDay = (group: Payment[]) => group.some(isSameDayPayment);
  const groupUrgent = (group: Payment[]) => group.some(isUrgentPayment);
  const groupEarliestDue = (group: Payment[]) =>
    Math.min(...group.map((p) => new Date(p.dueDate).getTime()));

  const pendingGroups = groupBackToBack(pendingPayments).sort((a, b) => {
    const aIsSameDay = groupSameDay(a);
    const bIsSameDay = groupSameDay(b);
    const aIsUrgent = groupUrgent(a);
    const bIsUrgent = groupUrgent(b);

    if (aIsSameDay && !bIsSameDay) return -1;
    if (!aIsSameDay && bIsSameDay) return 1;
    if (aIsUrgent && !bIsUrgent) return -1;
    if (!aIsUrgent && bIsUrgent) return 1;
    return groupEarliestDue(a) - groupEarliestDue(b);
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Payments</h1>
            <p className="text-gray-600">
              View your payment history and manage upcoming lesson payments
            </p>
          </div>
          <button
            onClick={() => setShowInfoModal(true)}
            className="flex items-center text-sm text-primary-600 hover:text-primary-700 bg-primary-50 px-4 py-2 rounded-lg"
          >
            <Info className="w-4 h-4 mr-2" />
            Payment Info
          </button>
        </div>
      </div>

      {/* Hour Packages Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Package className="w-5 h-5 mr-2 text-primary-600" />
            Your Hour Packages
          </h2>
          <button
            onClick={() => {
              setShowPackageModal(true);
              setPurchaseError('');
            }}
            className="flex items-center text-sm bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Buy Hour Package
          </button>
        </div>
        
        {hourPackages.length === 0 ? (
          <div className="bg-primary-50 rounded-lg shadow-sm border border-primary-200 p-6">
            <div className="text-center">
              <Package className="w-12 h-12 text-primary-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-primary-900 mb-2">Save with Hour Packages</h3>
              <p className="text-primary-700 mb-4">
                Purchase lesson hours in advance and save up to {maxDiscount > 0 ? `${maxDiscount}%` : '20%'}! Perfect for regular students.
              </p>
              <button
                onClick={() => {
              setShowPackageModal(true);
              setPurchaseError('');
            }}
                className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 font-medium"
              >
                View Package Options
              </button>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {hourPackages.map((pkg) => (
              <div key={pkg.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {pkg.subject.name} - {formatSubjectCategory(pkg.subject.category)}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {formatPackageType(pkg.packageType, pkg.totalHours)} - {pkg.isOnline ? 'Online' : 'In-Person'}
                    </p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getPackageStatusColor(pkg.status)}`}>
                    {pkg.status === 'PENDING' && <Clock className="w-3 h-3 mr-1" />}
                    {pkg.status}
                  </span>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Remaining Hours:</span>
                    <span className="font-medium text-gray-900">{formatHours(pkg.remainingHours)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Hours:</span>
                    <span className="text-gray-900">{pkg.totalHours}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Rate per Hour:</span>
                    <span className="text-gray-900">£{pkg.pricePerHour.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Discount:</span>
                    <span className="text-green-600 font-medium">{pkg.discountPercent}% off</span>
                  </div>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div 
                    className="bg-primary-600 h-2 rounded-full" 
                    style={{width: `${(pkg.remainingHours / pkg.totalHours) * 100}%`}}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 text-center">
                  {formatHours(pkg.usedHours)} of {pkg.totalHours} hours used
                </p>
                
                {/* PENDING status explanation */}
                {pkg.status === 'PENDING' && (
                  <div className="mt-3 pt-3 border-t border-yellow-200 bg-yellow-50 rounded p-2">
                    <p className="text-xs text-yellow-800 text-center">
                      <Clock className="w-3 h-3 inline mr-1" />
                      Awaiting payment confirmation - hours will be activated once payment is confirmed
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {statusBanner && (
        <div
          className={`mb-6 rounded-lg p-4 flex items-center justify-between border ${
            statusBanner.kind === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-yellow-50 border-yellow-200 text-yellow-800'
          }`}
        >
          <div className="flex items-center">
            {statusBanner.kind === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-3" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-3" />
            )}
            <p>{statusBanner.message}</p>
          </div>
          <button onClick={() => setStatusBanner(null)} className="opacity-70 hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Due Payments Section */}
      {pendingPayments.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-yellow-600" />
            Due Payments ({pendingPayments.length})
            {pendingPayments.some(p => isSameDayPayment(p)) && (
              <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                <AlertCircle className="w-3 h-3 mr-1" />
                Same-day payments first
              </span>
            )}
          </h2>
          <div className="grid gap-4">
            {pendingGroups.map((group) => {
              // A back-to-back block of 2+ lessons → one combined payment.
              if (group.length > 1) {
                const isSameDay = group.some(isSameDayPayment);
                const isUrgent = group.some(isUrgentPayment);
                const groupKey = group.map((p) => p.id).join(',');
                const groupTotal = group.reduce((sum, p) => sum + p.amount, 0);
                const earliestDue = group.reduce((min, p) =>
                  new Date(p.dueDate).getTime() < new Date(min.dueDate).getTime() ? p : min,
                );
                const cardClass = isSameDay
                  ? "bg-red-50 rounded-lg shadow-sm border border-red-300 p-6"
                  : isUrgent
                    ? "bg-yellow-50 rounded-lg shadow-sm border border-yellow-300 p-6"
                    : "bg-white rounded-lg shadow-sm border border-gray-200 p-6";

                return (
                  <div key={groupKey} className={cardClass}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {group.length} back-to-back lessons
                          </h3>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                            Combined payment
                          </span>
                          {isSameDay && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              SAME DAY
                            </span>
                          )}
                          {isUrgent && !isSameDay && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <Clock className="w-3 h-3 mr-1" />
                              URGENT
                            </span>
                          )}
                        </div>
                        <ul className="space-y-1 mb-2">
                          {group.map((p) => (
                            <li key={p.id} className="text-sm text-gray-600 flex justify-between gap-4">
                              <span>
                                {p.booking.subject.name} - {formatSubjectCategory(p.booking.subject.category)}
                                {' · '}
                                {formatDateTime(p.booking.startTime)}
                              </span>
                              <span className="text-gray-500 whitespace-nowrap">£{p.amount}</span>
                            </li>
                          ))}
                        </ul>
                        <p className={`text-sm ${isSameDay ? 'text-red-600 font-medium' : isUrgent ? 'text-yellow-600 font-medium' : 'text-gray-600'}`}>
                          Due: {formatDate(earliestDue.dueDate)}
                          {isSameDay && ' (TODAY)'}
                          {isUrgent && !isSameDay && ' (URGENT)'}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900 mb-2">
                          £{groupTotal}
                        </div>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Clock className="w-4 h-4" />
                          <span className="ml-1">PENDING</span>
                        </span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <button
                        onClick={() => handleGroupPaymentClick(group)}
                        disabled={startingCheckoutId === groupKey}
                        className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed ${
                          isSameDay
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : isUrgent
                              ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                              : 'bg-primary-600 text-white hover:bg-primary-700'
                        }`}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {startingCheckoutId === groupKey
                          ? 'Redirecting to Stripe...'
                          : `Pay for all ${group.length} lessons - £${groupTotal}`}
                      </button>
                      <p className="text-xs text-gray-500 text-center">
                        These lessons run back-to-back, so they're combined into one payment.
                      </p>
                    </div>
                  </div>
                );
              }

              const payment = group[0];
              const isSameDay = isSameDayPayment(payment);
              const isUrgent = isUrgentPayment(payment);
              const cardClass = isSameDay
                ? "bg-red-50 rounded-lg shadow-sm border border-red-300 p-6"
                : isUrgent
                  ? "bg-yellow-50 rounded-lg shadow-sm border border-yellow-300 p-6"
                  : "bg-white rounded-lg shadow-sm border border-gray-200 p-6";

              return (
                <div key={payment.id} className={cardClass}>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {payment.booking.subject.name} - {formatSubjectCategory(payment.booking.subject.category)}
                        </h3>
                        {isSameDay && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            SAME DAY
                          </span>
                        )}
                        {isUrgent && !isSameDay && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <Clock className="w-3 h-3 mr-1" />
                            URGENT
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        Lesson: {formatDateTime(payment.booking.startTime)} with {payment.booking.tutor.name}
                      </p>
                      <p className={`text-sm ${isSameDay ? 'text-red-600 font-medium' : isUrgent ? 'text-yellow-600 font-medium' : 'text-gray-600'}`}>
                        Due: {formatDate(payment.dueDate)}
                        {isSameDay && ' (TODAY)'}
                        {isUrgent && !isSameDay && ' (URGENT)'}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900 mb-2">
                        £{payment.amount}
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                        {getStatusIcon(payment.status)}
                        <span className="ml-1">{payment.status}</span>
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <button
                      onClick={() => handlePaymentClick(payment.id)}
                      disabled={startingCheckoutId === payment.id}
                      className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed ${
                        isSameDay
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : isUrgent
                            ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                            : 'bg-primary-600 text-white hover:bg-primary-700'
                      }`}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      {startingCheckoutId === payment.id
                        ? 'Redirecting to Stripe...'
                        : `${isSameDay ? 'PAY NOW (SAME DAY)' : isUrgent ? 'PAY NOW (URGENT)' : 'Pay Now'} - £${payment.amount}`}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Overdue Payments Section */}
      {overduePayments.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
            Overdue Payments ({overduePayments.length})
          </h2>
          <div className="grid gap-4">
            {overduePayments.map((payment) => (
              <div key={payment.id} className="bg-red-50 rounded-lg shadow-sm border border-red-200 p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {payment.booking.subject.name} - {formatSubjectCategory(payment.booking.subject.category)}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Lesson: {formatDateTime(payment.booking.startTime)} with {payment.booking.tutor.name}
                    </p>
                    <p className="text-sm text-red-600 font-medium">
                      Overdue since: {formatDate(payment.dueDate)}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-red-900 mb-2">
                      £{payment.amount}
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                      {getStatusIcon(payment.status)}
                      <span className="ml-1">{payment.status}</span>
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  <button
                    onClick={() => handlePaymentClick(payment.id)}
                    disabled={startingCheckoutId === payment.id}
                    className="w-full bg-red-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    {startingCheckoutId === payment.id ? 'Redirecting to Stripe...' : `Pay Now - £${payment.amount}`}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment History Section */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
          Payment History ({paidPayments.length})
        </h2>
        
        {paidPayments.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No payment history yet.</p>
            <p className="text-sm text-gray-500 mt-1">
              Completed payments will appear here.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lesson Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paid Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paidPayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {payment.booking.subject.name} - {formatSubjectCategory(payment.booking.subject.category)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDateTime(payment.booking.startTime)} with {payment.booking.tutor.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          £{payment.amount}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {payment.paidAt ? formatDate(payment.paidAt) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                          {getStatusIcon(payment.status)}
                          <span className="ml-1">{payment.status}</span>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Payment Information Modal */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Info className="w-6 h-6 mr-2 text-primary-600" />
                  How Payments Work
                </h2>
                <button
                  onClick={handleCloseInfoModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Payment Method Section */}
                <div className="bg-blue-50 rounded-lg p-5">
                  <h3 className="font-semibold text-lg text-blue-900 mb-3 flex items-center">
                    <CreditCard className="w-5 h-5 mr-2" />
                    How to Pay
                  </h3>
                  <p className="text-blue-800 mb-4">
                    Payments are processed securely by Stripe. Click "Pay Now" next to a lesson and you'll be taken to a secure checkout page.
                  </p>
                  <ol className="list-decimal list-inside space-y-1 text-blue-800 ml-2 text-sm">
                    <li>Click the "Pay Now" button next to your lesson</li>
                    <li>Pay with card, Apple Pay, or Google Pay on Stripe's secure page</li>
                    <li>You'll be returned here once payment is confirmed</li>
                    <li>Your booking is automatically marked as paid</li>
                  </ol>
                </div>

                {/* Pricing Section */}
                <div className="bg-green-50 rounded-lg p-5">
                  <h3 className="font-semibold text-lg text-green-900 mb-3">
                    Consistent & Simplified Pricing
                  </h3>
                  <p className="text-green-800 mb-3">
                    Our pricing structure is now simplified for your convenience:
                  </p>
                  <ul className="space-y-2 text-green-800">
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span>Fixed rates for each subject level (11+, GCSE, A-Level)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span>Same price for online and in-person lessons</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span>Consultation sessions at a reduced rate (£5)</span>
                    </li>
                  </ul>
                </div>

                {/* Payment Timing Section */}
                <div className="bg-yellow-50 rounded-lg p-5">
                  <h3 className="font-semibold text-lg text-yellow-900 mb-3 flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    When to Pay
                  </h3>
                  <p className="text-yellow-800 mb-3">
                    Parents should pay for lessons either:
                  </p>
                  <ul className="space-y-2 text-yellow-800">
                    <li className="flex items-start">
                      <span className="text-yellow-600 mr-2">•</span>
                      <span><strong>Before the lesson</strong> - Ideal for planning ahead</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-yellow-600 mr-2">•</span>
                      <span><strong>Right after the lesson</strong> - Within the same day</span>
                    </li>
                  </ul>
                  <p className="text-yellow-800 mt-3 bg-yellow-100 p-3 rounded">
                    <strong>Note:</strong> Same-day bookings will appear with a "SAME DAY" tag to remind you of urgent payment.
                  </p>
                </div>

                {/* Support Section */}
                <div className="bg-gray-50 rounded-lg p-5">
                  <h3 className="font-semibold text-lg text-gray-900 mb-3 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    Need Help?
                  </h3>
                  <p className="text-gray-700">
                    If you experience any issues with payments or have questions about pricing, please don't hesitate to reach out through the <strong>"Talk to Ethan"</strong> section. I'm here to help and ensure the payment process is smooth for everyone.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleCloseInfoModal}
                  className="btn btn-primary"
                >
                  Got it, thanks!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Package Purchase Modal */}
      {showPackageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Package className="w-6 h-6 mr-2 text-primary-600" />
                  Purchase Hour Package
                </h2>
                <button
                  onClick={() => setShowPackageModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Subject Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Subject
                  </label>
                  {subjects.length === 0 ? (
                    <p className="text-sm text-gray-600">
                      You haven't picked any subjects yet.{' '}
                      <a href="/profile" className="text-primary-600 hover:underline">Add some on your profile</a>{' '}
                      to buy hour packages here.
                    </p>
                  ) : (
                    <select
                      value={selectedSubject?.id || ''}
                      onChange={(e) => {
                        const subject = subjects.find(s => s.id === e.target.value);
                        setSelectedSubject(subject || null);
                        if (subject) {
                          fetchPackageOptions(subject.id, selectedDelivery === 'online');
                        }
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Choose a subject...</option>
                      {subjects.map((subject) => (
                        <option key={subject.id} value={subject.id}>
                          {subject.name} - {formatSubjectCategory(subject.category)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Delivery Method Selection */}
                {selectedSubject && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lesson Type
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="online"
                          checked={selectedDelivery === 'online'}
                          onChange={(e) => {
                            setSelectedDelivery('online');
                            fetchPackageOptions(selectedSubject.id, true);
                          }}
                          className="mr-2"
                        />
                        Online Lessons (£{selectedSubject.onlineRate}/hour)
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="in-person"
                          checked={selectedDelivery === 'in-person'}
                          onChange={(e) => {
                            setSelectedDelivery('in-person');
                            fetchPackageOptions(selectedSubject.id, false);
                          }}
                          className="mr-2"
                        />
                        In-Person Lessons (£{selectedSubject.inPersonRate}/hour)
                      </label>
                    </div>
                  </div>
                )}

                {/* Package Options */}
                {selectedSubject && packageOptions.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Package Options</h3>
                    {loadingPackages ? (
                      <div className="animate-pulse space-y-4">
                        {[1, 2, 3].map(i => (
                          <div key={i} className="h-24 bg-gray-200 rounded"></div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {packageOptions.map((option) => (
                          <div key={option.type} className="border border-gray-300 rounded-lg p-6 hover:border-primary-500 transition-colors">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h4 className="text-lg font-semibold text-gray-900">
                                  {option.name || `${option.hours} Hour Package`}
                                </h4>
                                <p className="text-sm text-green-600 font-medium">
                                  Save {option.discountPercent}% (£{option.discountAmount.toFixed(2)} off)
                                </p>
                              </div>
                              <div className="text-right">
                                <div className="text-sm text-gray-500 line-through">
                                  £{option.originalPrice.toFixed(2)}
                                </div>
                                <div className="text-2xl font-bold text-gray-900">
                                  £{option.discountedPrice.toFixed(2)}
                                </div>
                                <div className="text-sm text-gray-600">
                                  £{(option.discountedPrice / option.hours).toFixed(2)}/hour
                                </div>
                              </div>
                            </div>
                            <div className="flex justify-between items-center">
                              <div className="text-sm text-gray-600">
                                <p>Perfect for students planning {option.hours === 10 ? '10-15' : option.hours === 20 ? '20-30' : '30+'} lessons</p>
                                <p className="text-xs mt-1">Hours are specific to {selectedSubject.name} - {formatSubjectCategory(selectedSubject.category)}</p>
                              </div>
                              <button
                                onClick={() => handlePurchasePackage(option.type)}
                                disabled={purchasingPackage}
                                className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                              >
                                {purchasingPackage ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Purchasing...
                                  </>
                                ) : (
                                  'Purchase'
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Package Information */}
                    <div className="mt-6 bg-blue-50 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Important Information</h4>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>- Hours are subject-specific and cannot be transferred between subjects</li>
                        <li>- Payment is taken securely via Stripe (cards, Apple Pay, Google Pay)</li>
                        <li>- Hours activate automatically once Stripe confirms payment</li>
                        <li>- Hours are automatically deducted when lessons are confirmed</li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* Error Display */}
                {purchaseError && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                      <p className="text-red-800">{purchaseError}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowPackageModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PaymentsPage;